import { RefObject, useCallback, useMemo, useRef, useState } from 'react'
import { MenuItem, Select, Tooltip } from '@mui/material'
import {
  GridEventListener,
  GridFilterModel,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridValidRowModel,
} from '@mui/x-data-grid-premium'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import {
  BulkAction,
  ExtendedGridColDef,
} from 'Features/Projects/Table/BulkUpdateModal'
import { SegmentedControl } from 'Common/Components/SegmentedControl'
import { LineKind, RowProblem } from './lineRecalculation'

/**
 * Editing min/max cycle overrides on SKU production and Batch production.
 *
 * - The edit cell is a list that opens at once: type 1, 2, 4 or 8 to jump to
 *   it and Enter to choose, or None to clear the override.
 * - On a cell that isn't being edited, 1 / 2 / 4 / 8 set it straight away
 *   and Delete or Backspace clears it.
 * - Paste works (one value into a selected range, or a column from Excel):
 *   blank or "none" clears.
 * - Bulk update can clear, fix at the current cycle, or move one step.
 *
 * The rules stay in the API: whatever is typed is previewed there, and a
 * value it refuses is marked on its cell (lineRecalculation.ts).
 */

export const CYCLES = [1, 2, 4, 8]

const weeks = (cycle: number) => `${cycle} week${cycle === 1 ? '' : 's'}`

/**
 * A typed or pasted cycle: a number (the API refuses anything but 1, 2, 4
 * or 8, and the cell is marked), null for blank / "none" (no override), or
 * undefined for text that isn't a cycle at all, which paste then skips.
 */
export const parseCycle = (text: unknown): number | null | undefined => {
  if (text === null || text === undefined) return null
  const value = String(text).trim().toLowerCase()
  if (['', 'none', '-', '—'].includes(value)) return null
  const cycle = Number.parseFloat(value)
  return Number.isNaN(cycle) ? undefined : cycle
}

const NONE = ''

const CycleEditCell = ({ id, field, value, api }: GridRenderEditCellParams) => {
  const stop = () => {
    if (api.getCellMode(id, field) === 'edit')
      api.stopCellEditMode({ id, field })
  }
  return (
    <Select
      value={value ?? NONE}
      displayEmpty
      open
      fullWidth
      size="small"
      variant="outlined"
      onChange={async (event) => {
        const chosen = event.target.value
        await api.setEditCellValue({
          id,
          field,
          value: chosen === NONE ? null : Number(chosen),
        })
        stop()
      }}
      onClose={stop}
      renderValue={(chosen) =>
        chosen === NONE ? 'None' : weeks(Number(chosen))
      }
      sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
    >
      <MenuItem value={NONE}>None (no override)</MenuItem>
      {CYCLES.map((cycle) => (
        <MenuItem key={cycle} value={cycle}>
          {weeks(cycle)}
        </MenuItem>
      ))}
    </Select>
  )
}

const nextCycle = (cycle: unknown) => CYCLES.find((c) => c > Number(cycle)) ?? 8
const previousCycle = (cycle: unknown) =>
  [...CYCLES].reverse().find((c) => c < Number(cycle)) ?? 1

/** Bulk update for a min override: from each row's current min cycle. */
const minActions = (minInput: string): BulkAction[] => [
  { value: 'clear', label: 'Clear the override', apply: () => null },
  {
    value: 'current',
    label: 'Its current min cycle',
    apply: (row) => row[minInput] ?? null,
  },
  {
    value: 'slower',
    label: 'One step slower than its current min cycle',
    apply: (row) => nextCycle(row[minInput] ?? 1),
  },
]

/** Bulk update for a max override: from each row's current cycles. */
const maxActions = (minInput: string, maxInput: string): BulkAction[] => [
  { value: 'clear', label: 'Clear the override', apply: () => null },
  {
    value: 'current',
    label: 'Its current min cycle (fixes the cycle)',
    apply: (row) => row[minInput] ?? null,
  },
  {
    value: 'faster',
    label: 'One step faster than its current max cycle',
    apply: (row) => previousCycle(row[maxInput] ?? 8),
  },
]

type ProblemLookup = RefObject<
  ((id: GridRowId, field: string) => RowProblem[]) | null
>

/** A value with its problems (if any) as its tooltip. */
const withProblems =
  (problems: ProblemLookup) => (params: GridRenderCellParams) => {
    const found = problems.current?.(params.id, params.field) ?? []
    const shown = params.formattedValue ?? params.value ?? ''
    if (!found.length) return shown
    return (
      <Tooltip title={found.map((p) => p.message).join(' · ')}>
        <span>{shown === '' ? '?' : shown}</span>
      </Tooltip>
    )
  }

/**
 * A min or max override column. `minInput` / `maxInput` name the row's
 * resulting cycles, which the bulk actions work from.
 */
export const cycleOverrideColumn = ({
  field,
  headerName,
  description,
  bound,
  minInput,
  maxInput,
  problems,
}: {
  field: string
  headerName: string
  description: string
  bound: 'min' | 'max'
  minInput: string
  maxInput: string
  problems: ProblemLookup
}): ExtendedGridColDef => ({
  field,
  headerName,
  description,
  width: 112,
  type: 'number',
  editable: true,
  valueOptions: CYCLES,
  bulkActions:
    bound === 'min' ? minActions(minInput) : maxActions(minInput, maxInput),
  valueFormatter: (value: number | null) =>
    value === null || value === undefined ? '' : value,
  renderCell: withProblems(problems),
  renderEditCell: (params) => <CycleEditCell {...params} />,
  pastedValueParser: (text: string) => parseCycle(text),
})

/** Any column whose cells can carry a problem (MOQ, run rate, MBQ). */
export const showProblems = (
  column: ExtendedGridColDef,
  problems: ProblemLookup
): ExtendedGridColDef => ({ ...column, renderCell: withProblems(problems) })

export const SET_BY: Record<string, string> = {
  MOQ: 'The fastest cycle its MOQ allows: the natural cycle rounded up to 1, 2, 4 or 8.',
  MBQ: 'The fastest cycle its minimum batch size allows: the natural cycle rounded up to 1, 2, 4 or 8.',
  Batch:
    "Held back by its batch: the batch's minimum cycle is slower than this SKU group's own, and a SKU group runs only when its batch does.",
  Override: 'Moved by a min or max override.',
  FOQ: 'One minimum order lasts more than 8 weeks, so it runs every 8 weeks with a fixed quantity (FOQ).',
  'No demand': "Nothing is allocated to this line, so it isn't planned.",
}

/** What set each row's min cycle (the API's cycleSetBy), explained on hover. */
export const setByColumn = (): ExtendedGridColDef => ({
  field: 'cycleSetBy',
  headerName: 'Set by',
  description: 'What set the min cycle. Hover a value for why.',
  width: 104,
  type: 'string',
  renderCell: (params) =>
    params.value ? (
      <Tooltip title={SET_BY[params.value] ?? ''}>
        <span>{params.value}</span>
      </Tooltip>
    ) : (
      ''
    ),
})

export type QuickFilter = {
  value: string
  label: string
  field?: string
  equals?: string
}

const PRODUCT_FILTERS: QuickFilter[] = [
  { value: 'all', label: 'All' },
  {
    value: 'override',
    label: 'Overridden',
    field: 'cycleSetBy',
    equals: 'Override',
  },
  {
    value: 'batch',
    label: 'Held by batch',
    field: 'cycleSetBy',
    equals: 'Batch',
  },
  {
    value: 'foq',
    label: 'FOQ',
    field: 'replenishmentTypeCode',
    equals: 'FOQ',
  },
  {
    value: 'none',
    label: 'No demand',
    field: 'cycleSetBy',
    equals: 'No demand',
  },
]

export const QUICK_FILTERS: Record<LineKind | 'total', QuickFilter[]> = {
  products: PRODUCT_FILTERS,
  // Batches and SKU groups together: a batch shows when it or one of its
  // SKU groups matches.
  total: PRODUCT_FILTERS,
  batches: [
    { value: 'all', label: 'All' },
    {
      value: 'override',
      label: 'Overridden',
      field: 'cycleSetBy',
      equals: 'Override',
    },
    {
      value: 'foq',
      label: 'FOQ',
      field: 'replenishmentTypeCode',
      equals: 'FOQ',
    },
    {
      value: 'none',
      label: 'No demand',
      field: 'cycleSetBy',
      equals: 'No demand',
    },
  ],
}

const QUICK_ID = 'cycle-quick-filter'

/**
 * The grid wiring shared by SKU, Batch and Total production: the grid
 * API (for the preview and the quick filters), the keys that set a cycle
 * override, the problem lookup the columns' tooltips read, and the quick
 * filters with their counts.
 */
export const useCycleGrid = ({
  kind,
  data,
  overrideFields,
  previewApiRef,
  problemsAt,
}: {
  kind: LineKind | 'total'
  data: GridValidRowModel[]
  overrideFields: string[]
  previewApiRef: (ref: RefObject<GridApiPremium>) => void
  problemsAt: (id: GridRowId, field: string) => RowProblem[]
}) => {
  const grid = useRef<RefObject<GridApiPremium> | null>(null)
  const problems = useRef(problemsAt)
  problems.current = problemsAt
  const [quick, setQuick] = useState('all')

  const apiRefCallback = useCallback(
    (ref: RefObject<GridApiPremium>) => {
      grid.current = ref
      previewApiRef(ref)
    },
    [previewApiRef]
  )

  const onCellKeyDown: GridEventListener<'cellKeyDown'> = useCallback(
    (params, event) => {
      if (
        params.cellMode !== 'view' ||
        !params.isEditable ||
        !overrideFields.includes(params.field) ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      )
        return
      const value = ['Delete', 'Backspace'].includes(event.key)
        ? null
        : CYCLES.includes(Number(event.key))
          ? Number(event.key)
          : undefined
      const api = grid.current?.current
      if (value === undefined || !api) return
      event.defaultMuiPrevented = true
      event.preventDefault()
      const { id, field } = params
      // Through edit mode, so the change reaches the grid's pending edits
      // (processRowUpdate) like any other.
      api.startCellEditMode({ id, field })
      void Promise.resolve(api.setEditCellValue({ id, field, value })).then(
        () => api.stopCellEditMode({ id, field })
      )
    },
    [overrideFields]
  )

  const filters = QUICK_FILTERS[kind]
  const options = useMemo(
    () =>
      filters.map((filter) => {
        if (!filter.field) return { value: filter.value, label: filter.label }
        const count = data.filter(
          (row) => row[filter.field as string] === filter.equals
        ).length
        return { value: filter.value, label: `${filter.label} (${count})` }
      }),
    [data, filters]
  )

  const chooseQuick = useCallback(
    (value: string) => {
      const filter = filters.find((f) => f.value === value)
      setQuick(value)
      grid.current?.current?.setFilterModel({
        items: filter?.field
          ? [
              {
                id: QUICK_ID,
                field: filter.field,
                operator: 'equals',
                value: filter.equals,
              },
            ]
          : [],
      })
    },
    [filters]
  )

  // A filter changed from the grid's own panel: show which quick filter (if
  // any) it still is.
  const onFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      const [only, ...rest] = model.items
      const match =
        only && !rest.length
          ? filters.find(
              (f) => f.field === only.field && f.equals === only.value
            )
          : undefined
      setQuick(match?.value ?? (model.items.length ? '' : 'all'))
      return model
    },
    [filters]
  )

  const quickFilters = (
    <SegmentedControl
      label="Show rows"
      value={quick}
      options={options}
      onChange={chooseQuick}
    />
  )

  return {
    grid,
    apiRefCallback,
    onCellKeyDown,
    onFilterModelChange,
    problems,
    quickFilters,
  }
}
